import { ref, get, update, set, remove } from 'firebase/database';
import { database } from '../firebaseConfig';
import { mergePatch } from './dedupe';
import { ownerOf, indexOps } from './recordAccess';

// Admin-only merge of two contacts that are the same person. Runs ONLY when an admin clicks Merge on the
// Duplicates page; nothing merges automatically. The kept contact gets the other one's missing details,
// notes, timeline, tasks and every link (pipeline, transactions, lists, campaigns, appointments, referrals).
// A full copy of the removed record is kept at crm/mergedContacts/{id} in case it is ever needed.
const val = async (p) => (await get(ref(database, p))).val();
const nowIso = () => new Date().toISOString();

export const mergeContacts = async (keepId, otherId, uid) => {
  if (!keepId || !otherId || keepId === otherId) throw new Error('Pick two different contacts.');
  const keep = await val(`crm/contacts/${keepId}`); const other = await val(`crm/contacts/${otherId}`);
  if (!keep || !other) throw new Error('One of these contacts no longer exists. Refresh and try again.');
  const at = nowIso(); const ops = {};
  const patch = mergePatch(keep, { ...other, id: otherId });
  Object.entries(patch).forEach(([k, v]) => { ops[`crm/contacts/${keepId}/${k}`] = v; });
  ops[`crm/contacts/${keepId}/updatedAt`] = at;
  // Access: the kept contact keeps its owner; the other contact's owner and shares are added as shares,
  // so nobody who could see either record loses it. The removed record's index entries are cleared.
  const keepOwner = ownerOf(keep);
  const sharedWith = { ...(keep.sharedWith || {}), ...(other.sharedWith || {}) };
  if (ownerOf(other)) sharedWith[ownerOf(other)] = true;
  delete sharedWith[keepOwner];
  ops[`crm/contacts/${keepId}/sharedWith`] = Object.keys(sharedWith).length ? sharedWith : null;
  if (keepOwner && !keep.ownerUid) ops[`crm/contacts/${keepId}/ownerUid`] = keepOwner;
  Object.assign(ops, indexOps('contacts', keepId, { ownerUid: keepOwner, sharedWith }, keep));
  Object.assign(ops, indexOps('contacts', otherId, { ownerUid: null, sharedWith: {} }, other));
  // Timeline: every entry moves over with its original date.
  Object.entries((await val(`crm/activity/${otherId}`)) || {}).forEach(([k, a]) => { ops[`crm/activity/${keepId}/${k}`] = a; });
  // Pipeline records, campaigns, appointments, scheduled messages that point at the old contact.
  for (const path of ['crm/sellerInterests', 'crm/buyerInterests', 'crm/enrollments', 'crm/appointments', 'crm/scheduledMessages']) {
    Object.entries((await val(path)) || {}).forEach(([id, r]) => { if (r && r.contactId === otherId) ops[`${path}/${id}/contactId`] = keepId; });
  }
  // Transactions.
  Object.keys(other.transactionIds || {}).forEach((tx) => { ops[`transactions/${tx}/contactId`] = keepId; });
  // Lists.
  Object.entries((await val('crm/lists')) || {}).forEach(([id, l]) => {
    const ids = Array.isArray(l && l.contactIds) ? l.contactIds : null;
    if (ids && ids.includes(otherId)) ops[`crm/lists/${id}/contactIds`] = [...new Set(ids.map((x) => (x === otherId ? keepId : x)))];
  });
  // Other contacts: referrals and family links.
  Object.entries((await val('crm/contacts')) || {}).forEach(([id, c]) => {
    if (!c || id === otherId) return;
    if (c.referredById === otherId) ops[`crm/contacts/${id}/referredById`] = keepId;
    if (c.linkedContactIds && c.linkedContactIds[otherId] && id !== keepId) { ops[`crm/contacts/${id}/linkedContactIds/${otherId}`] = null; ops[`crm/contacts/${id}/linkedContactIds/${keepId}`] = c.linkedContactIds[otherId]; }
  });
  if (patch.linkedContactIds) { ops[`crm/contacts/${keepId}/linkedContactIds/${keepId}`] = null; ops[`crm/contacts/${keepId}/linkedContactIds/${otherId}`] = null; }
  // Tasks (personal task lists of every user) linked to the old contact.
  Object.entries((await val('users')) || {}).forEach(([u, rec]) => {
    Object.entries((rec && rec.tasks) || {}).forEach(([t, task]) => { if (task && task.crmContactId === otherId) ops[`users/${u}/tasks/${t}/crmContactId`] = keepId; });
  });
  await update(ref(database), ops);
  await set(ref(database, `crm/mergedContacts/${otherId}`), { ...other, mergedInto: keepId, mergedAt: at, mergedBy: uid || null });
  await remove(ref(database, `crm/activity/${otherId}`));
  await remove(ref(database, `crm/contacts/${otherId}`));
  const name = [other.firstName, other.lastName].filter(Boolean).join(' ') || other.name || 'a duplicate';
  await set(ref(database, `crm/activity/${keepId}/merge_${otherId}`), { type: 'merge', text: `Merged duplicate contact ${name} into this contact (notes, tasks, timeline and links moved here).`, at, by: uid || null });
  return keepId;
};
